import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Search, Crown, Skull, Flame, Trophy, Coins,
  Award, Eye, Calendar, Hash, Sparkles, BookOpen, Copy, ClipboardCheck,
  ShieldCheck, ExternalLink, Activity,
} from 'lucide-react';
import OnChainChip from '../components/OnChainChip';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SOLANA_EXPLORER = 'https://explorer.solana.com/tx/';

// ─────────────────────────────────────────────────────────────────────────────
// Reads live from the Chancellor Codex (chancellor_judgments collection):
//   GET /api/chancellor/wallet/:address
// On-chain fields (PlayerAccount PDA, ImmortalGhost PDA) are still pending the
// Anchor program — for now we derive the soul's title + stats from Codex data.
// Program ID: C5vRjZZAjG1uzcir9y2tw6dwMjH79fPwQgoKaSjEnACT
// ─────────────────────────────────────────────────────────────────────────────

const truncate = (s) => s ? `${s.slice(0, 5)}…${s.slice(-5)}` : '';

const StatTile = ({ label, value, accent, Icon }) => (
  <div
    className="relative px-4 py-4"
    style={{
      background: 'linear-gradient(180deg, rgba(20,12,4,0.95) 0%, rgba(8,4,2,0.98) 100%)',
      border: '1px solid rgba(140,100,40,0.4)',
      boxShadow: 'inset 0 1px 1px rgba(220,170,80,0.18), inset 0 -1px 1px rgba(0,0,0,0.7), 0 0 14px rgba(255,140,0,0.08)',
    }}
  >
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="w-3 h-3" style={{ color: accent }} />
      <p className="text-[9px] font-bold tracking-[0.25em] uppercase" style={{
        color: '#ffc870', fontFamily: "'Courier New', monospace",
        textShadow: '0 1px 2px rgba(0,0,0,0.95)',
      }}>{label}</p>
    </div>
    <p className="text-2xl sm:text-3xl font-black tracking-wide leading-none" style={{
      fontFamily: "'Impact', 'Arial Black', sans-serif",
      color: '#fff5dc',
      textShadow: `0 0 12px ${accent}, 0 0 22px rgba(255,140,0,0.55), 0 1px 2px rgba(0,0,0,1)`,
    }}>{value}</p>
  </div>
);

// Color-codes the decree type chip.
const DECREE_COLOR = {
  'trade narration': '#ffd86b',
  'reputation update': '#88e878',
  'ghost decree': '#ff8b6b',
  'coordination detected': '#ff4444',
  'ghost narration': '#a892ff',
};
const decreeColor = (d) => DECREE_COLOR[(d || '').toLowerCase()] || '#a08858';

// Activity level → glyph
const ACTIVITY_GLYPH = {
  dormant: { color: '#5a6878', label: 'DORMANT' },
  sleeping: { color: '#7a8898', label: 'SLEEPING' },
  casual: { color: '#a892ff', label: 'CASUAL' },
  active: { color: '#88e878', label: 'ACTIVE' },
  degen: { color: '#ff4444', label: 'DEGEN' },
  whale: { color: '#ffd86b', label: 'WHALE' },
};

// On-chain proof badge. Shows mocked state until Anchor program is live.
const ProofBadge = ({ tx, isMock }) => {
  if (isMock || !tx) {
    return (
      <span
        title="Mocked tx signature — Anchor program not yet deployed. Will become a verifiable on-chain link."
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[8px] font-bold tracking-[0.2em]"
        style={{
          fontFamily: "'Courier New', monospace",
          background: 'rgba(60,30,30,0.7)',
          border: '1px solid rgba(180,90,80,0.5)',
          color: '#ff8b6b',
          textShadow: '0 1px 2px rgba(0,0,0,0.9)',
        }}
        data-testid="soul-proof-mock"
      >
        <ShieldCheck className="w-3 h-3" />
        PREVIEW · MOCK
      </span>
    );
  }
  return (
    <a
      href={`${SOLANA_EXPLORER}${tx}?cluster=devnet`}
      target="_blank" rel="noopener noreferrer"
      data-testid="soul-proof-onchain"
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[8px] font-bold tracking-[0.2em] hover:brightness-125"
      style={{
        fontFamily: "'Courier New', monospace",
        background: 'rgba(40,40,20,0.7)',
        border: '1px solid rgba(120,200,90,0.5)',
        color: '#88e878',
      }}
    >
      <ShieldCheck className="w-3 h-3" />
      ON-CHAIN
      <ExternalLink className="w-2.5 h-2.5" />
    </a>
  );
};

const SearchView = ({ onSubmit }) => {
  const [val, setVal] = useState('');
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 relative" style={{ background: '#020100' }}>
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] group" style={{
        color: '#a08858', fontFamily: "'Courier New', monospace",
      }}>
        <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
        RETURN
      </Link>

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center max-w-xl">
        <p className="text-[10px] tracking-[0.4em] mb-3" style={{
          color: '#9a7a3a', fontFamily: "'Courier New', monospace",
        }}>◆ READ THE SOUL ◆</p>
        <h1 className="text-4xl sm:text-5xl font-black mb-3" style={{
          fontFamily: "'Cinzel', serif",
          background: 'linear-gradient(180deg, #f0d68a 0%, #c69544 50%, #6e4814 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          textShadow: '0 0 32px rgba(255,180,80,0.4)',
        }}>SUMMON A WALLET</h1>
        <p className="text-sm mb-8" style={{ color: '#bba377', fontFamily: "'Cinzel', serif" }}>
          Speak its address. The Codex will answer.
        </p>

        <form onSubmit={(e) => { e.preventDefault(); if (val.trim()) onSubmit(val.trim()); }} className="flex gap-2">
          <input
            type="text"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Wallet address…"
            data-testid="soul-search-input"
            autoFocus
            className="flex-1 px-4 py-3 text-sm font-mono outline-none transition-colors"
            style={{
              background: 'rgba(8,4,2,0.95)',
              border: '1px solid rgba(140,100,40,0.5)',
              color: '#f0d68a',
              borderRadius: 1,
              fontFamily: "'Courier New', monospace",
              caretColor: '#ffd86b',
            }}
          />
          <button
            type="submit"
            data-testid="soul-search-submit"
            className="px-5 py-3 text-[11px] font-bold tracking-[0.2em] transition-all hover:scale-[1.02]"
            style={{
              fontFamily: "'Cinzel', serif",
              background: 'linear-gradient(180deg, rgba(40,24,8,0.98) 0%, rgba(20,12,4,1) 100%)',
              border: '1px solid rgba(220,170,80,0.85)',
              color: '#ffd86b',
              textShadow: '0 0 8px rgba(255,180,80,0.6)',
              borderRadius: 1,
            }}
          >
            <Search className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />SEEK
          </button>
        </form>

        <p className="text-[10px] mt-5" style={{ color: '#7a6648' }}>
          or visit <Link to="/ghosts" className="underline" style={{ color: '#a08858' }}>The Hall of Ghosts</Link> · or browse the <Link to="/codex/analytics" className="underline" style={{ color: '#a08858' }}>public Codex</Link>.
        </p>
      </motion.div>
    </div>
  );
};

const NotFoundView = ({ address, onBack }) => (
  <div className="min-h-screen flex flex-col items-center justify-center px-5 text-center relative" style={{ background: '#020100' }}>
    <button onClick={onBack} className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] group" style={{
      color: '#a08858', fontFamily: "'Courier New', monospace",
    }} data-testid="soul-back">
      <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
      ANOTHER WALLET
    </button>

    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="max-w-md" data-testid="soul-not-found">
      <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center" style={{
        background: 'radial-gradient(circle at 50% 50%, rgba(140,40,30,0.4) 0%, transparent 70%)',
      }}>
        <Skull className="w-12 h-12" style={{ color: '#7a3838', filter: 'drop-shadow(0 0 12px rgba(180,40,40,0.5))' }} />
      </div>
      <h2 className="text-2xl sm:text-3xl font-black mb-3" style={{
        fontFamily: "'Cinzel', serif",
        color: '#f0d68a',
        textShadow: '0 0 16px rgba(255,180,80,0.5), 0 1px 2px rgba(0,0,0,1)',
      }}>SOUL NOT YET RECORDED</h2>
      <p className="text-[11px] font-mono mb-4" style={{ color: '#a08858', wordBreak: 'break-all' }}>{address}</p>
      <p className="text-sm mb-8 leading-relaxed" style={{ color: '#bba377', fontFamily: "'Cinzel', serif" }}>
        The Chancellor has not yet read this wallet. No judgment exists in the Codex.
        Ask the Chancellor in <Link to="/chancellor" className="underline" style={{ color: '#ffd86b' }}>The Chamber</Link> to forge the first.
      </p>
      <Link to="/soul" className="inline-block px-5 py-2.5 text-[11px] font-bold tracking-[0.2em]" style={{
        fontFamily: "'Cinzel', serif",
        background: 'linear-gradient(180deg, rgba(40,24,8,0.98) 0%, rgba(20,12,4,1) 100%)',
        border: '1px solid rgba(220,170,80,0.7)',
        color: '#ffd86b',
        textShadow: '0 0 8px rgba(255,180,80,0.6)',
        borderRadius: 1,
      }}>
        SEEK ANOTHER
      </Link>
    </motion.div>
  </div>
);

const LoadingView = ({ address, onBack }) => (
  <div className="min-h-screen flex flex-col items-center justify-center px-5 text-center relative" style={{ background: '#020100' }}>
    <button onClick={onBack} className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] group" style={{
      color: '#a08858', fontFamily: "'Courier New', monospace",
    }}>
      <ChevronLeft className="w-3.5 h-3.5" />
      RETURN
    </button>
    <p
      className="text-[11px] tracking-[0.32em] animate-pulse"
      style={{ color: '#a08858', fontFamily: "'Courier New', monospace" }}
      data-testid="soul-loading"
    >SUMMONING THE CODEX…</p>
    <p className="text-[10px] font-mono mt-3" style={{ color: '#5a4828', wordBreak: 'break-all' }}>{address}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// OutsiderSoulView — minimal page for wallets the Chancellor has judged but
// who have never burned, played, or initialized a player account on chain.
// "This soul walks beyond the sewers."
// ─────────────────────────────────────────────────────────────────────────────
const OutsiderSoulView = ({ address, items = [], onBack }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="min-h-screen text-white relative" style={{ background: '#020100' }}>
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(120,80,200,0.05) 0%, transparent 55%),
          radial-gradient(ellipse at 50% 50%, rgba(20,14,28,0.4) 0%, transparent 70%)
        `,
      }} />
      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 py-10 pb-32">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] mb-8 group" style={{
          color: 'rgba(168,146,255,0.78)', fontFamily: "'Courier New', monospace",
        }} data-testid="soul-back">
          <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
          RETURN
        </button>

        {/* OUTSIDER HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative mb-8 px-6 py-7"
          data-testid="outsider-soul-header"
          style={{
            background: 'linear-gradient(180deg, rgba(14,10,22,0.98) 0%, rgba(6,4,12,1) 100%)',
            border: '1px solid rgba(168,146,255,0.45)',
            boxShadow: 'inset 0 0 22px rgba(168,146,255,0.06), 0 0 32px rgba(120,90,220,0.12)',
            borderRadius: 2,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block rounded-full" style={{
              width: 6, height: 6, background: '#a892ff',
              boxShadow: '0 0 8px #a892ff',
              animation: 'pulse 1.6s ease-in-out infinite',
            }} />
            <span data-testid="outsider-badge" className="text-[10px] tracking-[0.32em] font-bold uppercase" style={{
              color: '#c5b6ff', fontFamily: "'JetBrains Mono','Courier New',monospace",
            }}>
              The Chancellor Has Spoken
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black leading-tight mb-3" style={{
            fontFamily: "'Cinzel', serif",
            background: 'linear-gradient(90deg, #c5b6ff, #ffffff, #a892ff)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 8px rgba(168,146,255,0.4))',
          }}>
            Outsider Soul
          </h1>
          <p className="text-[14px] leading-relaxed mb-4" style={{
            fontFamily: "'EB Garamond', serif", color: 'rgba(220,210,250,0.78)', fontStyle: 'italic',
          }}>
            This soul walks beyond the sewers. The Chancellor watches, but they have not yet descended.
          </p>
          <button onClick={copy} className="inline-flex items-center gap-1.5 text-[11px] font-mono group transition-colors" style={{ color: 'rgba(168,146,255,0.65)' }}>
            <span style={{ wordBreak: 'break-all' }}>{address}</span>
            {copied
              ? <ClipboardCheck className="w-3 h-3" style={{ color: '#88e878' }} />
              : <Copy className="w-3 h-3 opacity-50 group-hover:opacity-100" />}
          </button>
        </motion.div>

        {/* CTA */}
        <Link
          to="/arcade"
          data-testid="outsider-enter-sewers-cta"
          className="block w-full text-center rounded-lg py-3 font-bold uppercase tracking-[0.20em] text-[12px] mb-10 transition-all hover:scale-[1.01] active:scale-[0.99]"
          style={{
            background: 'linear-gradient(90deg, #ff6b00, #a892ff)',
            color: '#02010d',
            fontFamily: "'Cinzel', serif",
            boxShadow: '0 0 22px rgba(168,146,255,0.28), 0 0 12px rgba(255,107,0,0.15)',
          }}
        >
          ⛓ Enter the Sewers
        </Link>

        {/* JUDGMENTS LIST — only thing this page shows */}
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-3.5 h-3.5" style={{ color: '#c5b6ff' }} />
          <p className="text-[10px] font-bold tracking-[0.25em]" style={{
            color: '#c5b6ff', fontFamily: "'Cinzel', serif",
          }}>
            CHANCELLOR'S JUDGMENTS · {items.length}
          </p>
        </div>
        <div className="space-y-2" data-testid="outsider-judgments">
          {items.map((j) => {
            const accent = decreeColor(j.decree_type);
            const date = j.created_at ? new Date(j.created_at).toISOString().slice(0, 10) : '—';
            return (
              <div key={j.judgment_id} data-testid={`judgment-${j.judgment_id}`} className="px-4 py-3" style={{
                background: 'rgba(8,5,16,0.7)',
                border: '1px solid rgba(140,100,200,0.25)',
                borderLeft: `3px solid ${accent}`,
              }}>
                <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                  <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{
                    color: accent, fontFamily: "'Cinzel', serif",
                  }}>{j.decree_type}</span>
                  <span className="text-[10px] font-mono" style={{ color: 'rgba(168,146,255,0.55)' }}>{date}</span>
                  <span className="text-[9px] font-bold tracking-[0.2em] px-1.5 py-0.5" style={{
                    color: 'rgba(168,146,255,0.78)',
                    border: '1px solid rgba(168,146,255,0.25)',
                    fontFamily: "'Courier New', monospace",
                  }}>S{j.season_number}</span>
                  <div className="ml-auto">
                    <ProofBadge tx={j.tx_signature} isMock={j.tx_signature_is_mock !== false} />
                  </div>
                </div>
                {j.narration && (
                  <p className="text-[12.5px] mt-1 leading-relaxed" style={{
                    color: 'rgba(220,210,250,0.78)', fontFamily: "'EB Garamond', serif", fontStyle: 'italic',
                  }}>
                    "{j.narration}"
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


export default function SoulProfilePage() {
  const { address } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState(null);   // { wallet, total, items, is_player }
  const [status, setStatus] = useState('idle'); // idle | loading | not_found | error | ok

  useEffect(() => {
    if (!address) return;
    let live = true;
    setStatus('loading');
    setData(null);
    fetch(`${API_URL}/api/chancellor/wallet/${address}?limit=50`)
      .then(async (r) => {
        if (!r.ok) {
          if (r.status === 400) throw new Error('invalid');
          throw new Error('fetch_failed');
        }
        return r.json();
      })
      .then((d) => {
        if (!live) return;
        setData(d);
        setStatus(d && d.total > 0 ? 'ok' : 'not_found');
      })
      .catch(() => { if (live) setStatus('error'); });
    return () => { live = false; };
  }, [address]);

  // Search-mode if no address
  if (!address) {
    return <SearchView onSubmit={(addr) => navigate(`/soul/${addr}`)} />;
  }

  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/soul');
  };

  // Aggregate stats from real Codex judgments.
  const summary = useMemo(() => {
    if (!data || !data.items?.length) return null;
    const items = data.items;
    const reputation = items.reduce((s, j) => s + (j.reputation_delta || 0), 0);
    const seasons = new Set(items.map(j => j.season_number));
    const highestRisk = items.reduce((m, j) => Math.max(m, j.risk_score || 0), 0);
    // Derived "title" from latest decree / activity tier.
    const latest = items[0];
    const tier = (latest?.activity_level || latest?.snapshot?.activity_tier || '').toLowerCase();
    let title = 'Soul of the Codex';
    if (tier === 'whale') title = 'The Ash Sovereign';
    else if (tier === 'degen') title = 'Pyre-Walker';
    else if (tier === 'active') title = 'Warden of the Pyre';
    else if (tier === 'casual') title = 'Wanderer of Embers';
    else if (tier === 'sleeping' || tier === 'dormant') title = 'Silent Soul';

    const portfolio = latest?.snapshot?.portfolio_usd || 0;
    const sol = latest?.snapshot?.sol_balance || 0;
    return {
      title,
      reputation,
      seasonsCount: seasons.size,
      judgmentsCount: data.total,
      highestRisk,
      portfolio,
      sol,
      tier,
      latestDecree: latest?.decree_type,
    };
  }, [data]);

  if (status === 'loading') return <LoadingView address={address} onBack={handleBack} />;
  if (status === 'not_found' || status === 'error') return <NotFoundView address={address} onBack={handleBack} />;
  if (!data || !summary) return null;

  // Outsider Soul: judged but never burned/played/initialized.
  // The Chancellor sees them, but they're not yet a soul of the sewers.
  if (data.is_player === false) {
    return <OutsiderSoulView address={address} items={data.items} onBack={handleBack} />;
  }

  const tierStyle = ACTIVITY_GLYPH[summary.tier] || ACTIVITY_GLYPH.casual;

  const copyAddr = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen text-white relative" style={{ background: '#020100' }}>
      {/* Atmospheric backdrop */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(255,69,0,0.06) 0%, transparent 50%),
          radial-gradient(ellipse at 50% 50%, rgba(40,28,12,0.4) 0%, transparent 70%)
        `,
      }} />

      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 py-10 pb-32">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] mb-6 group" style={{
          color: '#a08858', fontFamily: "'Courier New', monospace",
        }} data-testid="soul-back">
          <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
          RETURN
        </button>

        {/* IDENTITY HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mb-8 px-6 py-6"
          data-testid="soul-identity-header"
          style={{
            background: 'linear-gradient(180deg, rgba(28,18,6,0.99) 0%, rgba(12,7,2,1) 100%)',
            border: '1px solid rgba(220,170,80,0.55)',
            boxShadow: `
              inset 0 1px 1px rgba(220,170,80,0.25),
              inset 0 -1px 1px rgba(0,0,0,0.7),
              0 0 0 2px rgba(8,4,2,1),
              0 0 0 3px rgba(180,140,55,0.5),
              0 0 32px rgba(255,140,0,0.18),
              0 6px 22px rgba(0,0,0,0.7)
            `,
            borderRadius: 2,
          }}
        >
          {['top-2 left-2 border-t border-l', 'top-2 right-2 border-t border-r', 'bottom-2 left-2 border-b border-l', 'bottom-2 right-2 border-b border-r'].map((cls, i) => (
            <div key={i} className={`absolute w-3 h-3 ${cls}`} style={{ borderColor: 'rgba(220,200,120,0.65)' }} />
          ))}

          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center" style={{
              background: 'radial-gradient(circle at 50% 40%, rgba(255,170,50,0.20) 0%, rgba(0,0,0,0.85) 70%)',
              border: '1px solid rgba(220,170,80,0.7)',
              boxShadow: 'inset 0 0 16px rgba(255,140,0,0.3), 0 0 14px rgba(255,140,0,0.4)',
            }}>
              {summary.tier === 'whale'
                ? <Crown className="w-7 h-7" style={{ color: '#ffd86b', filter: 'drop-shadow(0 0 8px #ffd86b)' }} />
                : <Skull className="w-7 h-7" style={{ color: '#a08858' }} />}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-block rounded-full" style={{
                  width: 6, height: 6, background: '#ff8c00',
                  boxShadow: '0 0 8px #ff8c00',
                  animation: 'pulse 1.6s ease-in-out infinite',
                }} />
                <span data-testid="soul-of-sewers-badge" className="text-[10px] tracking-[0.32em] font-bold uppercase" style={{
                  color: '#ffc870', fontFamily: "'JetBrains Mono','Courier New',monospace",
                  textShadow: '0 0 6px rgba(255,140,0,0.6)',
                }}>
                  Soul of the Sewers
                </span>
              </div>
              <div className="flex flex-wrap items-baseline gap-2 mb-2">
                <h1 className="text-2xl sm:text-3xl font-black leading-tight" style={{
                  fontFamily: "'Cinzel', serif",
                  color: '#fff5dc',
                  textShadow: '0 0 12px rgba(255,180,80,0.7), 0 0 24px rgba(255,140,0,0.5), 0 1px 2px rgba(0,0,0,1)',
                }}>
                  {summary.title}
                </h1>
                <span
                  data-testid="soul-tier-chip"
                  className="text-[10px] font-bold tracking-[0.25em] px-2 py-0.5"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    color: tierStyle.color,
                    background: `${tierStyle.color}22`,
                    border: `1px solid ${tierStyle.color}66`,
                    textShadow: `0 0 6px ${tierStyle.color}88`,
                  }}
                >
                  <Activity className="inline w-2.5 h-2.5 mr-1 -mt-0.5" />
                  {tierStyle.label}
                </span>
              </div>

              <button
                onClick={copyAddr}
                data-testid="soul-copy-address"
                className="inline-flex items-center gap-1.5 text-[11px] font-mono group transition-colors"
                style={{ color: '#a08858' }}
              >
                <span style={{ wordBreak: 'break-all' }}>{address}</span>
                {copied
                  ? <ClipboardCheck className="w-3 h-3" style={{ color: '#88e878' }} />
                  : <Copy className="w-3 h-3 opacity-50 group-hover:opacity-100" />}
              </button>
            </div>
          </div>
        </motion.div>

        {/* STATS GRID */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="soul-stats-grid">
          <StatTile label="REPUTATION" value={summary.reputation > 0 ? `+${summary.reputation}` : summary.reputation} accent="#ffd86b" Icon={Sparkles} />
          <StatTile label="JUDGMENTS" value={summary.judgmentsCount} accent="#ff8c00" Icon={BookOpen} />
          <StatTile label="SEASONS" value={summary.seasonsCount} accent="#a892ff" Icon={Calendar} />
          <StatTile label="HIGHEST RISK" value={summary.highestRisk} accent="#ff4444" Icon={Flame} />
        </div>

        {/* PORTFOLIO SNAPSHOT (latest) */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8 px-4 py-3"
          data-testid="soul-portfolio-snapshot"
          style={{
            background: 'rgba(8,4,2,0.6)',
            border: '1px solid rgba(140,100,40,0.3)',
            borderLeft: '3px solid rgba(220,170,80,0.6)',
          }}
        >
          <div>
            <p className="text-[9px] tracking-[0.25em]" style={{ color: '#9a7a3a', fontFamily: "'Courier New', monospace" }}>SOL</p>
            <p className="text-base font-bold" style={{ color: '#f0d68a', fontFamily: "'Cinzel', serif" }}>{summary.sol.toLocaleString('de-DE', { maximumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.25em]" style={{ color: '#9a7a3a', fontFamily: "'Courier New', monospace" }}>PORTFOLIO USD</p>
            <p className="text-base font-bold" style={{ color: '#f0d68a', fontFamily: "'Cinzel', serif" }}>${summary.portfolio.toLocaleString('de-DE')}</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.25em]" style={{ color: '#9a7a3a', fontFamily: "'Courier New', monospace" }}>LATEST DECREE</p>
            <p className="text-[12px] font-bold" style={{ color: decreeColor(summary.latestDecree), fontFamily: "'Cinzel', serif", textTransform: 'capitalize' }}>{summary.latestDecree || '—'}</p>
          </div>
        </div>

        {/* CHANCELLOR JUDGMENTS */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" style={{ color: '#ffc870' }} />
              <p className="text-[10px] font-bold tracking-[0.25em]" style={{
                color: '#ffc870', fontFamily: "'Cinzel', serif",
                textShadow: '0 1px 2px rgba(0,0,0,0.95)',
              }}>CHANCELLOR'S JUDGMENTS</p>
            </div>
            <Link to="/codex/analytics" className="text-[10px] tracking-[0.2em] hover:underline" style={{
              color: '#a08858', fontFamily: "'Courier New', monospace",
            }}>VIEW CODEX →</Link>
          </div>

          <div className="space-y-2" data-testid="soul-judgments">
            {data.items.map((j) => {
              const accent = decreeColor(j.decree_type);
              const date = j.created_at ? new Date(j.created_at).toISOString().slice(0, 10) : '—';
              return (
                <div
                  key={j.judgment_id}
                  data-testid={`judgment-${j.judgment_id}`}
                  className="px-4 py-3"
                  style={{
                    background: 'rgba(8,4,2,0.7)',
                    border: '1px solid rgba(140,100,40,0.3)',
                    borderLeft: `3px solid ${accent}`,
                  }}
                >
                  <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{
                      color: accent, fontFamily: "'Cinzel', serif",
                      textShadow: `0 0 6px ${accent}66`,
                    }}>{j.decree_type}</span>
                    <span className="text-[10px] font-mono" style={{ color: '#7a6648' }}>{date}</span>
                    <span className="text-[9px] font-bold tracking-[0.2em] px-1.5 py-0.5" style={{
                      color: '#a08858',
                      border: '1px solid rgba(140,100,40,0.4)',
                      fontFamily: "'Courier New', monospace",
                    }}>S{j.season_number}</span>
                    {Number.isFinite(j.risk_score) && (
                      <span className="text-[9px] font-bold tracking-[0.15em]" style={{
                        color: j.risk_score >= 70 ? '#ff6b6b' : j.risk_score >= 40 ? '#ffd86b' : '#88e878',
                        fontFamily: "'Courier New', monospace",
                      }}>RISK {j.risk_score}</span>
                    )}
                    {Number.isFinite(j.reputation_delta) && j.reputation_delta !== 0 && (
                      <span className="text-[9px] font-bold tracking-[0.15em]" style={{
                        color: j.reputation_delta > 0 ? '#88e878' : '#ff6b6b',
                        fontFamily: "'Courier New', monospace",
                      }}>{j.reputation_delta > 0 ? '+' : ''}{j.reputation_delta} REP</span>
                    )}
                    <div className="ml-auto">
                      <ProofBadge tx={j.tx_signature} isMock={j.tx_signature_is_mock !== false} />
                    </div>
                  </div>

                  <p className="text-[12px] leading-relaxed mb-2" style={{ color: '#dcc8a4', fontFamily: "'Cinzel', serif" }}>
                    {j.narration}
                  </p>

                  {j.behavior_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {j.behavior_tags.map((t) => (
                        <span key={t} className="text-[9px] tracking-[0.15em] px-1.5 py-0.5" style={{
                          color: '#a08858', fontFamily: "'Courier New', monospace",
                          background: 'rgba(40,28,12,0.5)',
                          border: '1px solid rgba(140,100,40,0.3)',
                        }}>{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* On-chain footer note */}
        <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
          <p className="text-[10px]" style={{ color: '#5a4828', fontFamily: "'Courier New', monospace", letterSpacing: '0.18em' }}>
            ◆ READ FROM THE CODEX · {data.total} JUDGMENT{data.total === 1 ? '' : 'S'} ◆
          </p>
          <OnChainChip target="program" testId="soul-onchain-program" />
        </div>
      </div>
    </div>
  );
}
